import { type FC, useEffect, useRef, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  InputLabel,
  Stack,
  Text,
  toast,
} from '@eluve/components';

import { useMiniAppApi } from '../../hooks';

import {
  CLINICAL_FINDINGS_AGENT_ID,
  CODE_SYSTEMS,
  ICD_10_CM_SYSTEM,
  codeSystemLabel,
} from './constants';
import { bumpFindings } from './findings-refresh';

/** Pending finding the modal edits (subset of the review-list item). */
export interface EditableFinding {
  resourceType: string;
  id: string;
  display: string;
  detail: string | null;
}

/** One code-search match from the backend. */
interface CodeMatch {
  system: string;
  code: string;
  display: string;
}

/** Pre-fill the system + code from the review list's `detail` string, which the
 *  backend formats as `"<LABEL> <code>"` (e.g. "ICD-10-CM R63.4"). */
function parseDetail(detail: string | null): {
  systemUrl: string | null;
  code: string;
} {
  if (!detail) return { systemUrl: null, code: '' };
  const match = CODE_SYSTEMS.find((s) => detail.startsWith(`${s.label} `));
  if (match) {
    return {
      systemUrl: match.url,
      code: detail.slice(match.label.length).trim(),
    };
  }
  return { systemUrl: null, code: '' };
}

/**
 * Modal to correct a pending finding's diagnosis — its display name and primary
 * code. The code is chosen from a validated ICD-10 search (reviewers don't have
 * to remember codes, and only real codes can be assigned); saves through the
 * reviewer's identity and the finding stays pending.
 */
export const EditFindingModal: FC<{
  tenantId: string;
  appointmentId: string;
  finding: EditableFinding;
  onClose: () => void;
  onSaved: () => void;
}> = ({ tenantId, appointmentId, finding, onClose, onSaved }) => {
  const api = useMiniAppApi(tenantId, CLINICAL_FINDINGS_AGENT_ID);
  const parsed = parseDetail(finding.detail);

  const [display, setDisplay] = useState(finding.display);
  const [systemUrl, setSystemUrl] = useState<string | null>(parsed.systemUrl);
  const [code, setCode] = useState(parsed.code);
  const [saving, setSaving] = useState(false);

  // Which system the search targets. Defaults to ICD-10-CM (the only set we can
  // search/validate today); the other buttons render disabled.
  const [searchSystem, setSearchSystem] = useState<string>(ICD_10_CM_SYSTEM);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CodeMatch[]>([]);
  const [searching, setSearching] = useState(false);
  // Drop results from a stale (superseded) query so the list never flickers
  // back to an older search.
  const latestQuery = useRef('');

  useEffect(() => {
    const term = query.trim();
    latestQuery.current = term;
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const res = await api.searchCodes(term, searchSystem);
        if (latestQuery.current !== term) return;
        setResults(res.status === 200 ? res.body.codes : []);
      } catch {
        if (latestQuery.current === term) setResults([]);
      } finally {
        if (latestQuery.current === term) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [api, query, searchSystem]);

  const pick = (match: CodeMatch) => {
    setSystemUrl(match.system);
    setCode(match.code);
    // Adopt the canonical description if the reviewer hasn't named it.
    if (!display.trim()) setDisplay(match.display);
    setQuery('');
    setResults([]);
  };

  const clearCode = () => {
    setSystemUrl(null);
    setCode('');
  };

  const save = async () => {
    if (!display.trim()) {
      toast.error('A name is required.');
      return;
    }
    setSaving(true);
    try {
      const res = await api.editFinding({
        appointmentId,
        resourceType: finding.resourceType,
        id: finding.id,
        display: display.trim(),
        system: code.trim() ? systemUrl : null,
        code: code.trim() || null,
      });
      if (res.status === 200) {
        toast.success('Finding updated.');
        bumpFindings();
        onSaved();
      } else if (res.status === 400) {
        // Backend rejected the code (not in the validated set).
        toast.error(res.body.error);
      } else {
        toast.error('Could not update the finding.');
      }
    } catch {
      toast.error('Could not update the finding.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit finding</DialogTitle>
        </DialogHeader>
        <Stack gap={4} className="pt-2">
          <InputLabel label="Diagnosis name">
            <Input
              value={display}
              onChange={(e) => setDisplay(e.target.value)}
            />
          </InputLabel>

          <Stack gap={1}>
            <InputLabel label="Coding system">
              <div className="flex flex-wrap items-center gap-1">
                {CODE_SYSTEMS.map((s) => (
                  <Button
                    key={s.url}
                    size="sm"
                    variant={
                      s.available && searchSystem === s.url
                        ? 'default'
                        : 'outline'
                    }
                    disabled={!s.available}
                    title={
                      s.available ? undefined : `No ${s.label} codes loaded yet`
                    }
                    onClick={() => {
                      setSearchSystem(s.url);
                      setQuery('');
                      setResults([]);
                    }}
                  >
                    {s.label}
                    {!s.available ? (
                      <span className="ml-1 text-2xs opacity-70">
                        (no codes)
                      </span>
                    ) : null}
                  </Button>
                ))}
              </div>
            </InputLabel>

            <InputLabel label="Diagnosis code">
              <Input
                value={query}
                placeholder="Search by name or code, e.g. “tuberculosis” or “Z20”"
                onChange={(e) => setQuery(e.target.value)}
              />
            </InputLabel>

            {/* Current selection — what gets saved. */}
            {code ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-border-secondary bg-muted/40 px-2 py-1.5">
                <Text size="sm">
                  <span className="font-medium">{code}</span>
                  {' · '}
                  {codeSystemLabel(systemUrl)}
                </Text>
                <Button size="sm" variant="ghost" onClick={clearCode}>
                  Remove
                </Button>
              </div>
            ) : (
              <Text size="xs" color="tertiary">
                No code assigned — search above to add one, or leave blank to
                keep just the name.
              </Text>
            )}

            {/* Search results. */}
            {query.trim().length >= 2 ? (
              <div className="max-h-56 overflow-y-auto rounded-md border border-border-secondary">
                {searching && results.length === 0 ? (
                  <Text size="xs" color="tertiary" className="px-2 py-2">
                    Searching…
                  </Text>
                ) : results.length === 0 ? (
                  <Text size="xs" color="tertiary" className="px-2 py-2">
                    No matching ICD-10-CM codes.
                  </Text>
                ) : (
                  <div className="flex flex-col divide-y divide-border-secondary">
                    {results.map((m) => (
                      <button
                        key={m.code}
                        type="button"
                        className="flex flex-col gap-0.5 px-2 py-1.5 text-left hover:bg-muted/60"
                        onClick={() => pick(m)}
                      >
                        <Text size="sm" weight="medium">
                          {m.code}
                        </Text>
                        <Text
                          size="xs"
                          color="tertiary"
                          className="line-clamp-2"
                        >
                          {m.display}
                        </Text>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </Stack>
        </Stack>
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
